import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const { toast } = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    // Simulate form submission
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We'll get back to you within 24 hours.",
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  const faqs = [
    {
      question: 'How quickly do you respond to inquiries?',
      answer: 'We usually respond within 24 hours on working days. Urgent mentorship-related queries are prioritized, and we make every effort to get back to you as soon as possible.'
    },
    {
      question: 'Can I get help with specific Physics problems?',
      answer: 'Yes. You can send your conceptual or numerical doubts, and our mentors will assist you through guided explanations or personalized sessions.'
    },
    {
      question: 'Do you offer technical support for the platform?',
      answer: 'Yes, we do. If you encounter any technical issues — such as login errors, missing content, or page glitches — please contact us via email or WhatsApp, and our team will resolve them promptly.'
    },
    {
      question: 'How can I contact you?',
      answer: 'You can reach us via WhatsApp or email — both details are available on our Contact Page. We aim to keep communication simple and responsive.'
    },
    {
      question: 'Can I schedule a call for assistance?',
      answer: 'Yes, we recommend reaching out via WhatsApp or email to schedule a call. This helps us ensure we can give you our full attention and be well-prepared to assist with your specific needs.'
    },
    {
      question: 'Do you respond on weekends?',
      answer: 'Our mentors occasionally respond on weekends, depending on availability. However, regular support hours apply on weekdays.'
    },
    {
      question: 'Will I receive confirmation after reaching out?',
      answer: 'Yes. You\'ll receive a personal acknowledgment (not an automated message) once we\'ve received your query. Expect a clear response or next steps shortly after.'
    }
  ];

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      content: 'aditya@physicsnotebook.in',
      description: 'For general inquiries or mentorship-related questions.'
    },
    {
      icon: Phone,
      title: 'Call',
      content: '+91 9309242445',
      description: 'Available for whatsapp messages'
    },
    {
      icon: MapPin,
      title: 'Visit',
      content: 'Pune, Maharashtra, India',
      description: 'Meeting by appointments only'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us - PhysicsNotebook.in | Get in Touch</title>
        <meta name="description" content="Contact PhysicsNotebook.in for support, questions, or feedback. Physics Notebook is here to help you with your physics learning journey. Reach out via email, phone, or contact form." />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-gradient section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-display text-4xl md:text-5xl font-semibold text-gray-900 mb-6"
          >
            Get in <span className="gradient-text">Touch</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Let’s make Physics simpler, structured, and stress-free — together.
            <br />
            Reach out to discuss your goals, get a personalized study plan, or book your first session.

          </motion.p>
        </div>
      </section>



      {/* Contact Info Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center bg-gray-50 rounded-xl p-8 card-hover"
              >
                <div className="bg-gradient-to-r from-[#123B7A] to-[#0EA5FF] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <info.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{info.title}</h3>
                <p className="text-lg font-medium text-[#123B7A] mb-2">{info.content}</p>
                <p className="text-gray-600">{info.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-3xl font-semibold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Find quick answers to common questions about Physics Notebook</p>
          </motion.div>

          <div className="space-y-6 max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="bg-white rounded-lg shadow-sm p-6 border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
